import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminOffers, adminCreateOffer, adminUpdateOffer, adminDeleteOffer, adminBooks, apiError } from "@/lib/api";
import { PrimaryBtn, Modal, fieldCls, labelCls } from "./ui";

const EMPTY = { nombre: "", descuento: 20, fecha_inicio: "", fecha_fin: "", libro_ids: [], activo: true };
const today = () => new Date().toISOString().slice(0, 10);
const estado = (o) => (!o.activo ? "Desactivada" : o.fecha_fin < today() ? "Finalizada" : o.fecha_inicio > today() ? "Programada" : "Activa");

export default function AdminOffers() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => adminOffers().then(setItems);
  useEffect(() => { load(); adminBooks().then((b) => setBooks(b.filter((x) => x.venta_directa))); }, []);

  const toggleBook = (id) => setForm((f) => ({ ...f, libro_ids: f.libro_ids.includes(id) ? f.libro_ids.filter((x) => x !== id) : [...f.libro_ids, id] }));
  const save = async (e) => {
    e.preventDefault();
    try {
      const d = { ...form, descuento: Number(form.descuento) };
      if (form.id) await adminUpdateOffer(form.id, d); else await adminCreateOffer(d);
      toast.success("Oferta guardada."); setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (o) => { if (!window.confirm(`¿Eliminar "${o.nombre}"?`)) return; await adminDeleteOffer(o.id); load(); };
  const names = (ids) => (ids.length ? ids.map((i) => books.find((b) => b.id === i)?.titulo).filter(Boolean).join(", ") : "Todos los ebooks");

  return (
    <div data-testid="offers-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-[#0f2544]">Ofertas programadas</h2>
        <PrimaryBtn onClick={() => setForm({ ...EMPTY, fecha_inicio: today() })} testId="new-offer-btn"><Plus size={15} /> Nueva oferta</PrimaryBtn>
      </div>
      {items.length === 0 ? <p className="text-sm text-[#8a8a83] rounded-xl border border-dashed border-[#0f2544]/15 bg-white py-8 text-center">No hay ofertas programadas.</p> : (
        <div className="space-y-2">
          {items.map((o) => (
            <div key={o.id} className="flex items-center gap-4 rounded-xl bg-white border border-[#0f2544]/8 px-4 py-3" data-testid={`offer-row-${o.id}`}>
              <span className="rounded-full bg-[#1f44e0] text-white text-xs font-semibold px-2.5 py-1">-{o.descuento}%</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#0f2544]">{o.nombre}</div>
                <div className="text-xs text-[#8a8a83] truncate">{o.fecha_inicio} → {o.fecha_fin} · {names(o.libro_ids)}</div>
              </div>
              <span className="text-xs text-[#4a4a45]" data-testid={`offer-status-${o.id}`}>{estado(o)}</span>
              <button onClick={() => setForm({ ...EMPTY, ...o })} className="p-1.5 text-[#8a8a83] hover:text-[#1f44e0]"><Pencil size={14} /></button>
              <button onClick={() => remove(o)} className="p-1.5 text-[#8a8a83] hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Editar oferta" : "Nueva oferta"}>
        {form && (
          <form onSubmit={save} className="space-y-4" data-testid="offer-form">
            <div><label className={labelCls}>Nombre de la oferta *</label><input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={fieldCls} placeholder="Ej: Black Friday" data-testid="offer-nombre" /></div>
            <div>
              <label className={labelCls}>Descuento</label>
              <select value={form.descuento} onChange={(e) => setForm({ ...form, descuento: e.target.value })} className={fieldCls} data-testid="offer-descuento">
                {[20, 30, 50].map((d) => <option key={d} value={d}>{d}%</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Desde *</label><input required type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className={fieldCls} data-testid="offer-inicio" /></div>
              <div><label className={labelCls}>Hasta (incluido) *</label><input required type="date" min={form.fecha_inicio} value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className={fieldCls} data-testid="offer-fin" /></div>
            </div>
            <div>
              <label className={labelCls}>Libros en oferta</label>
              <p className="text-xs text-[#8a8a83] mb-2">Sin marcar ninguno, la oferta se aplica a todos los ebooks en venta directa.</p>
              <div className="max-h-44 overflow-y-auto space-y-1 rounded-lg border border-[#0f2544]/10 p-2">
                {books.length === 0 && <p className="text-xs text-[#8a8a83]">Aún no hay libros con venta directa.</p>}
                {books.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.libro_ids.includes(b.id)} onChange={() => toggleBook(b.id)} className="accent-[#1f44e0]" data-testid={`offer-book-${b.slug}`} /> {b.titulo}</label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} className="accent-[#1f44e0]" /> Oferta activada</label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2.5 text-sm text-[#8a8a83]">Cancelar</button>
              <PrimaryBtn type="submit" testId="save-offer-btn">Guardar</PrimaryBtn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
